import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Target, ChartLineUp, Compass, SignOut, User, ArrowLeft, CheckCircle, XCircle,
  Sliders, UploadSimple, FolderOpen, UsersThree, Buildings, Broadcast, DotsThreeOutline, CaretRight,
  ShieldCheck, Eye, Storefront, EnvelopeSimple, Briefcase, Coins, Funnel,
} from "@phosphor-icons/react";
import { ListChecks } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { isDAv2 } from "@/lib/da";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const NAV = [
  { to: "/", label: "Commando", icon: Target, testid: "nav-commando", end: true },
  { to: "/taches", label: "Tâches du jour", icon: ListChecks, testid: "nav-taches", description: "Relances, anniversaires, post-signature, stand-by, BOS / hebdo" },
  { to: "/dossiers", label: "Dossiers", icon: FolderOpen, testid: "nav-dossiers" },
  { to: "/leads", label: "Leads", icon: Funnel, testid: "nav-leads", description: "Prospects du simulateur et historique des simulations" },
  { to: "/apporteurs", label: "Apporteurs", icon: UsersThree, testid: "nav-apporteurs" },
  // V4.44 — Cartographie des entreprises (niveau stratégique au-dessus des apporteurs)
  { to: "/entreprises", label: "Entreprises", icon: Buildings, testid: "nav-entreprises" },
  { to: "/cash", label: "Cash", icon: ChartLineUp, testid: "nav-cash" },
  { to: "/bos", label: "Cockpit BOS", icon: Briefcase, testid: "nav-bos", description: "Pipe Production : qui relancer, pourquoi, quand" },
  { to: "/pilotage", label: "Pilotage", icon: Compass, testid: "nav-pilotage" },
];

// V6.2 — Items secondaires accessibles via l'onglet "Plus" en mobile
// (et sous la section "Archives" sur desktop). Architecture réutilisable pour
// tout nouvel item paramètre / secondaire.
// V6.3 — Ajout entrée Admin (visible uniquement si user.is_admin).
const SECONDARY_NAV_BASE = [
  { to: "/suivi-hebdo", label: "Suivi hebdo", icon: EnvelopeSimple, testid: "nav-suivi-hebdo", description: "Points hebdomadaires clients (mandats signés)" },
  { to: "/marketplace", label: "Marketplace", icon: Storefront, testid: "nav-marketplace", description: "Leads anonymisés partagés dans votre réseau privé" },
  { to: "/parametres/mon-simulateur", label: "Mon simulateur", icon: Broadcast, testid: "nav-simulator-settings", description: "Personnalisez votre lien public et votre bouton de rappel" },
  { to: "/objectifs", label: "Objectifs", icon: Sliders, testid: "nav-objectifs", description: "Vos objectifs de production" },
  { to: "/commissions", label: "Commissions", icon: Coins, testid: "nav-commissions", description: "Commissions acquises, paiements enregistrés, exports" },
  { to: "/signes", label: "Dossiers signés", icon: CheckCircle, testid: "nav-signes", description: "Historique des dossiers gagnés" },
  { to: "/perdus", label: "Dossiers perdus", icon: XCircle, testid: "nav-perdus", description: "Historique des dossiers perdus" },
  { to: "/import", label: "Import CSV", icon: UploadSimple, testid: "nav-import", description: "Import de dossiers ou de leads" },
];

const ADMIN_NAV_ITEMS = [
  { to: "/admin/courtiers", label: "Courtiers autorisés", icon: ShieldCheck, testid: "nav-admin-brokers", description: "Gérez la whitelist et les accès" },
  { to: "/admin/scoring-v8", label: "Scoring V8 (shadow)", icon: Eye, testid: "nav-admin-scoring-v8", description: "Observation du moteur V8 — lecture seule" },
];

function useSecondaryNav() {
  const { user } = useAuth();
  return user?.is_admin ? [...ADMIN_NAV_ITEMS, ...SECONDARY_NAV_BASE] : SECONDARY_NAV_BASE;
}

// DA v2 — sidebar regroupée (aucune entrée supprimée) : 5 groupes lisibles, accès dossiers accéléré
const DA2_GROUPS = [
  ["Piloter", ["/", "/pilotage", "/cash", "/objectifs", "/commissions"]],
  ["Dossiers", ["/dossiers", "/leads", "/bos", "/suivi-hebdo", "/signes", "/perdus"]],
  ["Réseau", ["/apporteurs", "/entreprises", "/marketplace"]],
  ["Outils", ["/parametres/mon-simulateur", "/import"]],
  ["Administration", ["/admin/courtiers", "/admin/scoring-v8"]],
];

function DA2Sidebar({ items }) {
  const byTo = Object.fromEntries(items.map((i) => [i.to, i]));
  return (
    <nav className="flex flex-col gap-0.5 flex-1" data-testid="da2-sidebar-nav">
      {DA2_GROUPS.map(([group, tos]) => {
        const rows = tos.map((t) => byTo[t]).filter(Boolean);
        if (!rows.length) return null;
        return (
          <div key={group} className="mb-3">
            <div className="overline mb-1 px-2.5">{group}</div>
            {rows.map(({ to, label, icon: Icon, testid, end }) => (
              <NavLink key={to} to={to} end={!!end} data-testid={`desktop-${testid}`} className="flex items-center gap-2.5 text-sm">
                <Icon size={17} weight="light" />
                <span>{label}</span>
              </NavLink>
            ))}
          </div>
        );
      })}
    </nav>
  );
}

export function BottomNav() {
  const [moreOpen, setMoreOpen] = useState(false);
  const navigate = useNavigate();
  const secondaryNav = useSecondaryNav();

  return (
    <>
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 md:hidden glass safe-bottom"
        data-testid="bottom-nav"
      >
        <div className="flex justify-around items-center h-[64px] px-1">
          {NAV.map(({ to, label, icon: Icon, testid, end }) => (
            <NavLink
              key={to}
              to={to}
              end={!!end}
              data-testid={testid}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 flex-1 min-w-0 h-full transition-colors ${
                  isActive ? "text-[color:var(--cf-primary)]" : "text-slate-400"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon size={20} weight={isActive ? "fill" : "regular"} />
                  <span className="text-[9.5px] tracking-tight font-semibold truncate max-w-full px-0.5">{label}</span>
                </>
              )}
            </NavLink>
          ))}
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            data-testid="nav-more"
            className="flex flex-col items-center justify-center gap-0.5 flex-1 min-w-0 h-full transition-colors text-slate-400 active:text-[color:var(--cf-primary)]"
          >
            <DotsThreeOutline size={20} weight="regular" />
            <span className="text-[9.5px] tracking-tight font-semibold">Plus</span>
          </button>
        </div>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent
          side="bottom"
          className="p-0 rounded-t-3xl border-0 max-h-[85vh] bg-white"
          data-testid="more-sheet"
        >
          <div className="pt-2 pb-3">
            {/* Poignée */}
            <div className="mx-auto w-12 h-1.5 rounded-full bg-slate-200 mb-4" />
            <div className="px-5 pb-3">
              <div className="text-[10px] uppercase tracking-widest font-extrabold text-slate-400">Menu</div>
              <SheetTitle asChild>
                <div className="font-display text-xl font-extrabold text-slate-900">Plus</div>
              </SheetTitle>
              <SheetDescription className="sr-only">
                Accès aux paramètres et archives : Mon simulateur, Objectifs, Dossiers signés, Dossiers perdus, Import CSV.
              </SheetDescription>
            </div>
            <div className="px-3 pb-2 safe-bottom">
              {secondaryNav.map(({ to, label, icon: Icon, testid, description }) => (
                <button
                  key={to}
                  type="button"
                  data-testid={`more-${testid}`}
                  onClick={() => { setMoreOpen(false); navigate(to); }}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-xl active:bg-slate-100 transition text-left"
                >
                  <div className="w-11 h-11 rounded-xl bg-[color:var(--cf-primary-soft)] flex items-center justify-center shrink-0">
                    <Icon size={20} color="var(--cf-primary)" weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-extrabold text-slate-900">{label}</div>
                    {description && (
                      <div className="text-[11.5px] text-slate-500 truncate">{description}</div>
                    )}
                  </div>
                  <CaretRight size={16} className="text-slate-300 shrink-0" weight="bold" />
                </button>
              ))}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

export function DesktopSidebar() {
  const { user, logout } = useAuth();
  const secondaryNav = useSecondaryNav();

  return (
    <aside
      className="hidden md:flex flex-col w-64 h-screen fixed left-0 top-0 z-40 border-r border-slate-200 bg-white p-5"
      data-testid="desktop-sidebar"
    >
      <div className="flex items-center gap-2.5 mb-10">
        <div className="w-9 h-9 rounded-xl bg-[color:var(--cf-primary)] flex items-center justify-center shadow-[0_4px_12px_rgba(45,110,255,0.3)]">
          <div className="w-3.5 h-3.5 bg-white rounded-sm rotate-45" />
        </div>
        <div>
          <div className="font-display text-base font-extrabold leading-none text-slate-900">Crédit Flow</div>
          <div className="text-[10px] text-slate-400 tracking-widest uppercase mt-1 font-semibold">Pilotage</div>
        </div>
      </div>

      {isDAv2() ? <DA2Sidebar items={[...NAV, ...secondaryNav]} /> : (
      <nav className="flex flex-col gap-1 flex-1">
        <div className="overline mb-2 px-2">Navigation</div>
        {NAV.map(({ to, label, icon: Icon, testid, end }) => (
          <NavLink
            key={to}
            to={to}
            end={!!end}
            data-testid={`desktop-${testid}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-sm font-semibold ${
                isActive
                  ? "bg-[color:var(--cf-primary-soft)] text-[color:var(--cf-primary)]"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon size={18} weight={isActive ? "fill" : "regular"} />
                <span>{label}</span>
              </>
            )}
          </NavLink>
        ))}

        <div className="overline mb-2 px-2 mt-6">Paramètres & Archives</div>
        {secondaryNav.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`desktop-${testid}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-sm font-semibold ${
                isActive
                  ? "bg-[color:var(--cf-primary-soft)] text-[color:var(--cf-primary)]"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
              }`
            }
          >
            <Icon size={18} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
      )}

      <div className="border-t border-slate-100 pt-4 mt-4">
        <div className="flex items-center gap-3 p-2" data-testid="sidebar-user">
          {user?.picture ? (
            <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full ring-2 ring-[color:var(--cf-primary-soft)]" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-[color:var(--cf-primary-soft)] flex items-center justify-center">
              <User size={16} color="var(--cf-primary)" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate text-slate-900">{user?.name}</div>
            <div className="text-[11px] text-slate-400 truncate">{user?.email}</div>
          </div>
          <button
            onClick={logout}
            data-testid="sidebar-logout"
            className="text-slate-400 hover:text-[color:var(--cf-danger)] p-1"
            title="Déconnexion"
          >
            <SignOut size={18} />
          </button>
        </div>
      </div>
    </aside>
  );
}

export function MobileHeader({ title, subtitle, right, backTo, centerTitle }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Fiche-style header: back arrow + centered title
  if (centerTitle || backTo) {
    return (
      <header className="md:hidden glass sticky top-0 z-40 safe-top" data-testid="mobile-header">
        <div className="grid grid-cols-[40px_1fr_40px] items-center px-4 h-16">
          <button
            onClick={() => (backTo ? navigate(backTo) : navigate(-1))}
            className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center"
            data-testid="mobile-back"
          >
            <ArrowLeft size={18} color="var(--cf-text)" weight="bold" />
          </button>
          <div className="font-display text-[17px] font-extrabold text-slate-900 text-center truncate">
            {title}
          </div>
          <div className="flex justify-end">
            {right}
          </div>
        </div>
      </header>
    );
  }

  // Home-style header: big name left + avatar right
  return (
    <header className="md:hidden glass sticky top-0 z-40 safe-top" data-testid="mobile-header">
      <div className="flex items-center justify-between px-5 h-[72px]">
        <div className="flex-1 min-w-0">
          {subtitle && (
            <div className="text-[12px] text-slate-500 font-semibold flex items-center gap-1.5">
              {subtitle}
            </div>
          )}
          <div className="font-display text-[22px] font-extrabold text-slate-900 leading-tight truncate">
            {title}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {right}
          {user?.picture ? (
            <button onClick={logout} data-testid="mobile-logout" title="Déconnexion">
              <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full ring-2 ring-[color:var(--cf-primary-soft)]" />
            </button>
          ) : (
            <button
              onClick={logout}
              data-testid="mobile-logout"
              className="w-10 h-10 rounded-full bg-white border-2 border-[color:var(--cf-primary-border)] flex items-center justify-center"
            >
              <User size={18} color="var(--cf-primary)" weight="bold" />
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
